import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { DISPLAY_TYPE_OPTIONS } from "../../../api/SwayyOptionsAPI";
import { SwayyOptionDisplayType } from "../../../models/options/SwayyCatalogOption";
import { SwayyCatalogOptionsStore } from "../../../stores/SwayyCatalogOptionsStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { SwayyPageHeader, SwayyTableEmptyState } from "../../components/SwayyPageShell";

const primaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
} as const;

const secondaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
} as const;

const inputSx = {
  borderRadius: swayyRadii.md,
  fontFamily: swayyFont,
  fontSize: 13,
  height: 32,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-input": {
    py: 0.5,
    height: 32,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: swayyColors.border },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
} as const;

const SwayyCatalogOptions = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const storeRef = useRef(new SwayyCatalogOptionsStore());
  const store = storeRef.current;
  const debouncedSearch = useDebounce(store.searchDraft, 400);
  const [createOpen, setCreateOpen] = useState(false);
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  useEffect(() => {
    if (!canViewCatalog) return;
    void store.loadOptions().then((error) => {
      if (error) {
        showErrorRef.current("Error", "Failed to load options.");
      }
    });
    return () => store.dispose();
  }, [canViewCatalog, store]);

  useEffect(() => {
    if (!canViewCatalog) return;
    store.setSearch(debouncedSearch);
  }, [canViewCatalog, debouncedSearch, store]);

  const handleCreate = async () => {
    if (!store.canCreateOption) {
      showError("Validation", "Option name is required.");
      return;
    }
    const result = await store.createOption();
    if (result.error) {
      showError("Error", "Failed to create option.");
      return;
    }
    setCreateOpen(false);
    showSuccess("Option created.");
    if (result.optionId) navigate(`/catalog-options/${result.optionId}`);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    store.resetCreateForm();
  };

  if (!canViewCatalog) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <SwayyPageHeader
        title="Options"
        description="Create catalog options and link them on categories so products can use them."
        actions={
          canEditCatalog ? (
            <AppButton
              label="Add option"
              variant="primary"
              onClick={() => setCreateOpen(true)}
              customStyles={primaryBtnSx}
            />
          ) : undefined
        }
      />

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
        }}
      >
        <TextField
          size="small"
          placeholder="Search options"
          value={store.searchDraft}
          onChange={(event) => store.setSearchDraft(event.target.value)}
          fullWidth
          sx={{
            maxWidth: 360,
            fontFamily: swayyFont,
            "& .MuiInputBase-root": { height: 32, fontSize: 13 },
          }}
        />
      </Paper>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          overflow: "hidden",
        }}
      >
        {store.listState === "loading" || store.listState === "idle" ? (
          <Box sx={{ p: 2 }}>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </Box>
        ) : store.listState === "error" ? (
          <SwayyTableEmptyState
            title="Couldn't load options"
            description={store.errorMessage || "Network Error"}
            actionLabel="Try again"
            onAction={() => {
              void store.loadOptions().then((error) => {
                if (error) {
                  showError("Error", "Failed to load options.");
                }
              });
            }}
            danger
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Display</TableCell>
                <TableCell>Values</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.filteredOptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <CustomTypography
                      variant="body2"
                      color={swayyColors.textSecondary}
                    >
                      No options yet. Add an option to get started.
                    </CustomTypography>
                  </TableCell>
                </TableRow>
              ) : (
                store.filteredOptions.map((option) => (
                  <TableRow
                    key={option.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/catalog-options/${option.id}`)}
                  >
                    <TableCell>
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        fontWeight={700}
                      >
                        {option.name}
                      </CustomTypography>
                    </TableCell>
                    <TableCell>{option.displayType}</TableCell>
                    <TableCell>{option.values.length}</TableCell>
                    <TableCell>
                      {option.isActive ? "Active" : "Inactive"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog
        open={createOpen}
        onClose={handleCloseCreate}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add option</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <OutlinedInput
              value={store.createForm.name}
              onChange={(event) =>
                store.updateCreateForm("name", event.target.value)
              }
              placeholder="Option name"
              fullWidth
              sx={inputSx}
            />
            <AppSearchDropdownMulti
              isMultiSelect={false}
              title="Display type"
              list={DISPLAY_TYPE_OPTIONS.map((item) => ({
                id: item.id,
                value: item.value,
              }))}
              value={[
                {
                  id: store.createForm.displayType,
                  value:
                    DISPLAY_TYPE_OPTIONS.find(
                      (item) => item.id === store.createForm.displayType,
                    )?.value || store.createForm.displayType,
                },
              ]}
              local
              accentColor={swayyColors.pine}
              borderColor={swayyColors.border}
              handleSelect={(selected) => {
                const next = selected[0]?.id as
                  | SwayyOptionDisplayType
                  | undefined;
                if (next) store.updateCreateForm("displayType", next);
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseCreate}
            customStyles={secondaryBtnSx}
          />
          <AppButton
            label={store.isSaving ? "Creating..." : "Create"}
            variant="primary"
            disabled={store.isSaving}
            onClick={() => void handleCreate()}
            customStyles={primaryBtnSx}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default observer(SwayyCatalogOptions);
